import { defineStore } from 'pinia';
import IodineGrouperService, { InitData } from 'src/services/IodineGrouperService';
import { GrouperService } from 'src/services/GrouperService';
import { computed, ref } from 'vue';
import { GrouperData } from 'src/contracts/grouperData';
import { addDiagnosisCodes } from 'src/common/codeUtil';
import { Subscription } from 'rxjs';
import { Services } from 'src/services/service-manager';
import { DischargeStatus } from 'src/contracts/discharge-status';
import { isEmpty } from 'src/common/arrayUtil';
import { useLayoutStore } from 'stores/layout-store';
import { CalculateDrgRequestDto } from 'src/contracts/calculateDrgRequestDto';
import { DrgResponseDto } from 'src/contracts/drgResponseDto';
import { DiagnosisCode } from 'src/contracts/diagnosisCode';
import { ProcedureCode } from 'src/contracts/procedureCode';
import { PatientData } from 'src/contracts/patientData';
import { DrgData } from 'src/contracts/drgData';
import {GrouperInfo} from 'src/contracts/grouperInfo';
import {DrgInfo} from 'src/contracts/drgInfo';
import { EditData } from 'src/contracts/edit';

export const useGrouperStore = defineStore('grouperstore', () => {

  const diagnosisCodes = ref<DiagnosisCode[]>([]);
  const resequence = ref<boolean>(false);

  const grouperInfo= ref<GrouperInfo>()
  const drgInfo = ref<DrgInfo[]>([]);

  const getCodesCount = computed(() => diagnosisCodes.value.length + procedureCodes.value.length);
  const procedureCodes = ref<ProcedureCode[]>([]);
  const edits = ref<EditData[]>([]);
  const primaryDRG = ref<DrgData>({});
  const secondaryDRG = ref<DrgData>({});
  const patient = ref<PatientData | undefined>({
    name: 'Default Patient',
    dischargeDate: '00/00/0000',
    sex: 'N',
    codingSystem: 'AAA'
  });
  const dischargeStatuses = ref<DischargeStatus[]>([]);
  const getDischargeStatuses = computed(() => dischargeStatuses.value);
  const dischargeStatus = ref<DischargeStatus | undefined>();

  function resetDRGData() {
    diagnosisCodes.value = [];
    procedureCodes.value = [];
    drgInfo.value = [];
  }

  // temporary helper function
  function searchCodes(text: string | undefined): string {
    if (text === 'E87.3') {
      return 'Diagnosis Code E87.3';
    } else if (text === 'J43.9') {
      return 'Diagnosis Code J43.9';
    } else if (text === 'E08.10') {
      return 'Diagnosis Code E08.10';
    } else if (text === 'J18.9') {
      return 'Diagnosis Code J18.9';
    } else {
      return '';
    }
  }

  function addDiagnosisCode(code: string | undefined, description: string | undefined, poa: string | undefined): void {
    diagnosisCodes.value.push({
      pdx: [],
      drgModifier: [],
      poa: poa || undefined,
      quality: [],
      risk: [],
      id: diagnosisCodes.value.length,
      action: '',
      code,
      description
    })
  }

  function changeResequence() {
    resequence.value = !resequence.value
    useGrouperStore().calculateDrg(true, true);
  }

  function addProcedureCode(code: string | undefined, description: string | undefined): void {
    procedureCodes.value?.push({
      action: '',
      code,
      id: procedureCodes.value?.length || 0,
      impact: [],
      slot: [],
      description
    })
  }

  async function calculateDrg(calculatePrimaryDrg: boolean, calculateSecondaryDrg: boolean): Promise<void> {
    const grouperData: CalculateDrgRequestDto  = {
      diagnoses: diagnosisCodes.value,
      procedures: procedureCodes.value,
      calculatePrimaryDrg,
      calculateSecondaryDrg,
      resequence: resequence.value
    };

    const grouperService = Services.service(GrouperService);
    const drgResponseDto = await grouperService.calculateDrg(grouperData);
    console.log(`DRG Calculate Response: ${JSON.stringify(drgResponseDto)}`)
    handleDrgEdits(drgResponseDto);
    handleDrgData(drgResponseDto);
  }

  async function saveDrg(): Promise<void> {
    const grouperData: any  = {
      diagnoses: convertDiagnosis(),
      procedures: procedureCodes.value,
      grouper_state: grouperInfo.value?.grouperState,
      drg_info: drgInfo.value
    };

    console.log(JSON.stringify(grouperData));

    const grouperService = Services.service(GrouperService);
    const drgResponseDto = await grouperService.saveDrg(grouperData);
    console.log(`DRG save Response: ${JSON.stringify(drgResponseDto)}`)
  }

  function convertDiagnosis(): any[] {
    return diagnosisCodes.value.map(diagnose => {
      return {
        id: diagnose.id,
        present_on_admission: diagnose.poa,
        code: diagnose.code,

      }
    })
  }

  let setVisitContextSubscription: Subscription | undefined;
  let addCodeSubscription: Subscription | undefined;

  function createAddCodeSubscription() {
    addCodeSubscription = IodineGrouperService.addCodes$.subscribe(
      async (addCodeData: GrouperData) => {
        console.log(`Adding code(s): ${JSON.stringify(addCodeData)}`);

        addDiagnosisCodes(diagnosisCodes.value, addCodeData.diagnosisCodes);
      }
    );
  }

  function createContextSubscription() {
    setVisitContextSubscription = IodineGrouperService.setVisitContext$.subscribe(
      async (initData: InitData) => {
        console.log('Resetting DRG data');
        resetDRGData();
        useLayoutStore().changeMode(initData.mode, false);
        Services.setDefaultConfig(initData.endpoint, initData.visitId, initData.facilityCode);

        const grouperService = Services.service(GrouperService);
        const response = await grouperService.getGrouperData();

        if (isEmpty(dischargeStatuses.value)) {
          dischargeStatuses.value = await grouperService.getDischargeStatuses();
        }

        drgInfo.value = response.drgInfo || [];
        grouperInfo.value = response.grouperInfo;

        diagnosisCodes.value = response.diagnosisCodes || [];
        procedureCodes.value = response.procedureCodes || [];
        patient.value = response.patient;
      }
    );
  }

  function handleDrgEdits(drgResponseDto: DrgResponseDto[] | undefined): void {
    // reset edits
    edits.value = [];

    drgResponseDto?.forEach(drg => {
      drg.edits?.edit.forEach(edit => {
        edits.value.push(edit);

        const index = parseInt(edit.position || '-1');
        switch (edit.content_type) {
          case 'diagnoses':
            const diagnosisCode = diagnosisCodes.value?.at(index);
            if (diagnosisCode) {
              diagnosisCode.edit = edit;
            }
            break;
          case 'procedures':
            const procedureCode = procedureCodes.value?.at(index);
            if (procedureCode) {
              procedureCode.edit = edit;
            }
            break;
          default:
            console.log(`Unexpected content type: ${edit.contentType}`);
            break;
            // throw new Error(`Unexpected content type: ${edit.contentType}`);
        }
      })
    })
  }

  function handleDrgData(drgResponseDto: DrgResponseDto[] | undefined): void {
    if (!drgResponseDto) {
      return;
    }

    // I think the first entry in the response is the primary/MS DRG and the second is the secondary/APR DRG.
    // Need to confirm with product though.
    if (drgResponseDto[0]) {
      primaryDRG.value = {
        drg: drgResponseDto[0].drg || '',
        description: drgResponseDto[0].drg_description || '',
        mdc: drgResponseDto[0].mdc || '',
        gmlos: drgResponseDto[0].gmlos || 0,
        weight: drgResponseDto[0].drg_weight || 0,
        reimbursement: drgResponseDto[0].drg_reimbursement || undefined,
        type: drgResponseDto[0].drg_type || ''
      }
    }

    if (drgResponseDto[1]) {
      secondaryDRG.value = {
        drg: drgResponseDto[1].drg || '',
        description: drgResponseDto[1].drg_description || '',
        gmlos: drgResponseDto[1].gmlos || 0,
        weight: drgResponseDto[1].drg_weight || 0,
        soi: drgResponseDto[1].soi || 0,
        rom: drgResponseDto[1].rom || 0
      }
    }
  }

  function $onMount() {
    createContextSubscription();
    createAddCodeSubscription();
  }

  function $reset() {
    setVisitContextSubscription && setVisitContextSubscription.unsubscribe();
    addCodeSubscription && addCodeSubscription.unsubscribe();
  }

  return {
    diagnosisCodes,
    getCodesCount,
    procedureCodes,
    edits,
    resequence,
    primaryDRG,
    secondaryDRG,
    patient,
    searchCodes,
    addDiagnosisCode,
    addProcedureCode,
    $reset,
    $onMount,
    setVisitContextSubscription,
    addCodeSubscription,
    calculateDrg,
    saveDrg,
    changeResequence,
    getDischargeStatuses,
    dischargeStatus
  }
})
